<script setup lang="ts">
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import { default as PButton } from 'primevue/button'
import { default as PMessage } from 'primevue/message'
import { default as PSkeletonCard } from 'primevue/skeleton'
import { computed, ref } from 'vue'
import type School from '@/core/domain/school/core/School.ts'
import CSchoolPeriodCreate from '@/presentation/components/schools/CSchoolPeriodCreate.vue'
import { useDialog } from 'primevue/usedialog'
import { storeToRefs } from 'pinia'
import { useSchoolPeriodStore } from '@/presentation/stores/school/schoolPeriod.ts'
import CSchoolPeriodsItem from '@/presentation/components/schools/CSchoolPeriodsItem.vue'

// COMPOSABLES
const dialog = useDialog()
const schoolPeriodStore = useSchoolPeriodStore()

// STORE REFS
const { schoolPeriods, needsFreshData, schoolPeriodStoreError, schoolPeriodStoreIsLoading } =
  storeToRefs(schoolPeriodStore)

// LOCAL STATE
const localLoading = ref(false)
const localError = ref('')

// PROPS
const props = defineProps<{
  school: School
}>()

// COMPUTED
const isEmpty = computed(() => !localLoading.value && schoolPeriods.value.length === 0)

const isLoading = computed(() => localLoading.value || schoolPeriodStoreIsLoading.value)

const errorMessage = computed(() => {
  if (localError.value) return localError.value
  if (schoolPeriodStoreError.value?.hasError) return schoolPeriodStoreError.value.message
  return ''
})

const currentPeriod = computed(() =>
  schoolPeriods.value.find((period) => period.schoolYear.isCurrent)
)

// METHODS
const createSchoolPeriod = () => {
  const dialogRef = dialog.open(CSchoolPeriodCreate, {
    props: {
      header: 'Associer une période scolaire',
      dismissableMask: true,
      draggable: false,
      modal: true,
      style: {
        width: '75vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      }
    },
    data: {
      school: props.school,
      schoolPeriods: schoolPeriods.value
    }
  })
}

/**
 * Charge les périodes scolaires
 * Note: Les périodes sont déjà chargées par le router guard au premier chargement
 * Cette fonction est utilisée pour rafraîchir manuellement les données
 * @param forceRefresh Force le rafraîchissement des données
 */
const fetchSchoolPeriods = async (forceRefresh = false) => {
  if (!props.school?.id) return

  try {
    localLoading.value = true
    localError.value = ''

    await schoolPeriodStore.fetchAllSchoolPeriodsForSchool(props.school.id, forceRefresh)
  } catch (err) {
    localError.value = err instanceof Error ? err.message : 'Erreur lors du chargement des périodes'
    console.error('Erreur lors du chargement des périodes scolaires:', err)
  } finally {
    localLoading.value = false
  }
}
</script>

<template>
  <c-base-block :toggleable="false" title="Périodes scolaires">
    <div class="col-span-12 flex w-full gap-2">
      <p-button
        label="Associer à une année scolaire"
        icon="pi pi-plus"
        severity="primary"
        size="small"
        :disabled="isLoading"
        @click="createSchoolPeriod"
      />
      <p-button
        icon="pi pi-refresh"
        text
        rounded
        severity="secondary"
        :disabled="isLoading"
        @click="fetchSchoolPeriods(true)"
        v-tooltip.top="'Rafraîchir les données'"
      />
    </div>

    <!-- Message d'erreur -->
    <p-message v-if="errorMessage" severity="error" class="mb-4 w-full">
      {{ errorMessage }}
    </p-message>

    <!-- État de chargement -->
    <div v-if="isLoading" class="col-span-12 grid w-full grid-cols-subgrid gap-4">
      <div class="col-span-12 md:col-span-6 lg:col-span-3" v-for="i in 4" :key="i">
        <p-skeleton-card class="h-40" />
      </div>
    </div>

    <!-- Pas de périodes -->
    <div v-else-if="isEmpty" class="flex w-full flex-col items-center gap-4 py-8">
      <i class="pi pi-calendar text-surface-500 dark:text-surface-300 text-4xl"></i>
      <p>Aucune période scolaire associée à cet établissement.</p>
      <p>Commencez par associer une année scolaire à l'établissement.</p>
    </div>

    <!-- Liste des périodes -->
    <div v-else class="w-full pt-6">
      <!-- Périodes par ordre chronologique -->
      <div class="grid grid-cols-12 gap-4">
        <div
          class="col-span-4 md:col-span-6 lg:col-span-3"
          v-for="(schoolPeriod, index) in schoolPeriods"
          :key="schoolPeriod.schoolPeriodId || index"
        >
          <c-school-periods-item
            :school-period="schoolPeriod"
            :school-year="schoolPeriod.schoolYear"
          />
        </div>
      </div>
    </div>
  </c-base-block>
</template>
